import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { makeStyles, Container, IconButton } from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import T from './tokens'
import Reveal from './Reveal'

const promos = [
  { src: '/static/img/home-new/promos/promo-flash.png', altKey: 'promotions.alt1' },
  { src: '/static/img/home-new/promos/promo-spx.png', altKey: 'promotions.alt2' },
  { src: '/static/img/home-new/promos/promo-ems.png', altKey: 'promotions.alt3' },
]

const GAP = 20 // px between slides

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '60px 0',
    background: T.color.surface,
    [theme.breakpoints.down('sm')]: {
      padding: '40px 0',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 32,
    fontWeight: 500,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 32px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 22,
      margin: '0 0 20px',
    },
  },
  carousel: {
    position: 'relative',
    maxWidth: T.container,
    margin: '0 auto',
  },
  viewport: {
    overflow: 'hidden',
  },
  track: {
    display: 'flex',
    width: '100%',
    transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  page: {
    flex: '0 0 100%',
    display: 'flex',
    gap: `${GAP}px`,
    alignItems: 'stretch',
    minWidth: 0,
  },
  slide: {
    flex: '0 0 auto',
    '& img': {
      width: '100%',
      height: 'auto',
      aspectRatio: '1 / 1',
      objectFit: 'cover',
      display: 'block',
      borderRadius: T.radius.lg,
      boxShadow: T.shadow.card,
    },
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.9) !important',
    boxShadow: T.shadow.md,
    zIndex: 2,
    cursor: 'pointer',
    '&:hover': {
      background: 'rgba(255,255,255,1) !important',
    },
  },
  prevBtn: {
    left: -20,
    [theme.breakpoints.down('sm')]: { left: 4 },
  },
  nextBtn: {
    right: -20,
    [theme.breakpoints.down('sm')]: { right: 4 },
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    // gap 0: each dot is a 48px-wide hit box (below), so button centers are
    // 48px apart — the spacing Lighthouse tap-targets needs — while the
    // transparent horizontal padding keeps the visible dots compact.
    gap: 0,
    marginTop: 16,
  },
  // 48×24 tap target (Lighthouse tap-targets: ≥48px pitch so finger zones
  // don't overlap). The fill is painted only in the inner content box via
  // background-clip, so the VISIBLE dot stays a 10px circle — the enlargement
  // is an invisible hit area, not a bigger dot.
  dot: {
    width: 48,
    height: 24,
    boxSizing: 'border-box',
    padding: '7px 19px',
    backgroundClip: 'content-box',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    background: T.color.line,
    transition: 'background 0.3s',
  },
  dotActive: {
    background: T.color.brand,
    backgroundClip: 'content-box',
    borderRadius: 5,
    // 48 − 20px horizontal padding = 28px visible pill (unchanged look).
    padding: '7px 10px',
  },
}))

const PromotionsNew = () => {
  const classes = useStyles()
  const { t } = useTranslation('home')
  // Responsive items-per-view: 3 desktop / 2 tablet / 1 mobile.
  // Read window width directly (SSR-safe default = desktop) and update on
  // resize — more reliable than useMediaQuery under SSR/hydration.
  const [perView, setPerView] = useState(3)
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth
      setPerView(w >= 960 ? 3 : w >= 600 ? 2 : 1)
    }
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [])

  const pages = Math.ceil(promos.length / perView)
  const showControls = pages > 1
  const [page, setPage] = useState(0)
  const timerRef = useRef(null)

  // Keep page in range when perView (breakpoint) changes — clamp to the
  // nearest valid page instead of jumping back to the start.
  useEffect(() => {
    setPage((prev) => Math.min(prev, pages - 1))
  }, [pages])

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current)
    if (pages <= 1) return
    timerRef.current = setInterval(() => {
      setPage((prev) => (prev + 1) % pages)
    }, 5000)
  }, [pages])

  useEffect(() => {
    startTimer()
    return () => clearInterval(timerRef.current)
  }, [startTimer])

  const goTo = (idx) => {
    setPage(idx)
    startTimer()
  }
  // Wraps at both ends — matches the existing autoplay timer, which already
  // wraps continuously, so manual paging stays consistent with it.
  const prev = () => goTo((page - 1 + pages) % pages)
  const next = () => goTo((page + 1) % pages)

  // One group per page, each holding up to `perView` promos. Rendering every
  // page (instead of only the current slice) lets the track slide with a
  // real transform instead of a jump-cut remount.
  const pageGroups = Array.from({ length: pages }, (_, i) =>
    promos.slice(i * perView, i * perView + perView)
  )
  // Fixed per-slide width so a partially-filled last page keeps items at
  // 1/perView width (left-aligned) instead of stretching.
  const slideWidth =
    perView === 1
      ? '100%'
      : `calc((100% - ${(perView - 1) * GAP}px) / ${perView})`

  return (
    <section className={classes.root}>
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <Reveal>
          <h2 className={classes.heading}>{t('promotions.heading')}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className={classes.carousel}>
            <div className={classes.viewport}>
              <div
                className={classes.track}
                style={{ transform: `translateX(-${page * 100}%)` }}
              >
                {pageGroups.map((group, i) => (
                  <div
                    key={i}
                    className={classes.page}
                    style={{ justifyContent: group.length < perView ? 'flex-start' : 'center' }}
                  >
                    {group.map((p) => (
                      <div key={p.altKey} className={classes.slide} style={{ width: slideWidth }}>
                        <picture style={{ display: 'contents' }}>
                          <source srcSet={p.src.replace(/\.png$/, '.webp')} type="image/webp" />
                          <img
                            src={p.src}
                            alt={t(p.altKey)}
                            width={760}
                            height={760}
                            loading="lazy"
                            decoding="async"
                          />
                        </picture>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {showControls && (
              <>
                <IconButton
                  className={`${classes.navBtn} ${classes.prevBtn}`}
                  onClick={prev}
                  aria-label={t('promotions.prevAria')}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  className={`${classes.navBtn} ${classes.nextBtn}`}
                  onClick={next}
                  aria-label={t('promotions.nextAria')}
                >
                  <ChevronRightIcon />
                </IconButton>
                <div className={classes.dots}>
                  {Array.from({ length: pages }).map((_, i) => (
                    <button
                      key={i}
                      className={`${classes.dot} ${i === page ? classes.dotActive : ''}`}
                      onClick={() => goTo(i)}
                      aria-label={t('promotions.dotAria', { n: i + 1 })}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </Reveal>
      </Container>
    </section>
  )
}

export default PromotionsNew
